import { Link, useFetcher } from '@remix-run/react'
import { AnimatePresence, motion } from 'framer-motion'
import { ChevronRightIcon } from 'lucide-react'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { makeShopOwnPath } from '~/helpers/routes'
import { useShop } from '~/hooks/data/use-shop'
import { useOnboardingChecklist } from '~/hooks/use-onboarding-checklist'
import { useTailwindBreakpoint } from '~/hooks/useTailwindBreakpoint'
import { Button } from '~/ui'
import { cn } from '~/utils/classnames'

import { useNavbarContext } from './navbar-context'

export function NavbarOnboardingChecklist() {
  const { t } = useTranslation()
  const shop = useShop()
  const fetcher = useFetcher()

  const { setIsOpen } = useNavbarContext()
  const isMd = useTailwindBreakpoint('md')

  const { steps, allCompleted } = useOnboardingChecklist()

  const handlePublishShop = useCallback(() => {
    fetcher.submit(
      { _intent: 'publishShop' },
      {
        method: 'post',
        action: makeShopOwnPath(shop, '/settings/general'),
      }
    )
  }, [fetcher, shop])

  return (
    <div
      className={cn(
        'bg-over/2 border-border/4 text-foreground shrink-0 overflow-hidden rounded-xl border p-2',
        'text-xs'
      )}
    >
      <h3 className="flex items-center gap-1 text-xs font-medium">
        <span>
          {allCompleted
            ? t('page.settings.visibility.store_ready_for_publishing')
            : t('page.settings.visibility.getting_store_ready')}
        </span>
      </h3>
      <AnimatePresence>
        {!allCompleted && (
          <motion.div
            initial={{ height: 0 }}
            animate={{ height: 'auto' }}
            exit={{ height: 0 }}
            transition={{ type: 'spring', bounce: 0.1, visualDuration: 0.25 }}
            className="overflow-hidden"
          >
            <ol className="mt-2 mb-1 flex flex-col">
              <AnimatePresence>
                {steps.map((step) =>
                  step.checked ? null : (
                    <motion.li
                      key={step.id}
                      initial={{ height: 0, filter: 'blur(4px)' }}
                      animate={{
                        height: 'auto',
                        filter: 'blur(0px)',
                      }}
                      exit={{ height: 0, filter: 'blur(4px)' }}
                      className="overflow-hidden"
                      transition={{
                        type: 'spring',
                        bounce: 0.1,
                        visualDuration: 0.25,
                      }}
                    >
                      <Link
                        to={step.link}
                        className={cn(
                          'group flex items-center justify-between gap-1.5 rounded-md py-1 opacity-64 transition hover:opacity-100',
                          step.checked && 'pointer-events-none'
                        )}
                        onClick={() => {
                          if (isMd) return

                          setIsOpen(false)
                        }}
                      >
                        <span
                          className={cn('pr-4', step.checked && 'line-through')}
                        >
                          {step.title}
                        </span>
                        <ChevronRightIcon className="translate-x-2 opacity-0 transition-transform ease-out group-hover:translate-x-0 group-hover:opacity-100" />
                      </Link>
                    </motion.li>
                  )
                )}
              </AnimatePresence>
            </ol>
          </motion.div>
        )}
      </AnimatePresence>
      <Button
        disabled={!allCompleted || fetcher.state !== 'idle'}
        className="mt-2 w-full"
        onClick={handlePublishShop}
        isLoading={fetcher.state !== 'idle'}
      >
        {t('action.publish')}
      </Button>
    </div>
  )
}
